import { Table, type TableGridRecordProps } from '@sqs/rosetta-compositions';
import { Flex, Text } from '@sqs/rosetta-primitives';
import React from 'react';
import { usePlatformBreakpoint } from '../../hooks/usePlatformBreakpoint';
import { useI18n } from '../../i18n';
import { OrgUser } from '../../models/SqspUser';
import { AcuityLoader } from '../common/AcuityLoader';
import { EmptyResults } from '../common/EmptyResults';

export interface SchedulingInstanceUsersListTableDataProps {
  readonly hasLoaded: boolean;
  readonly schedulingInstanceUsers: OrgUser[];
}

export const SchedulingInstanceUsersListTableData = ({
  hasLoaded,
  schedulingInstanceUsers
}: SchedulingInstanceUsersListTableDataProps) => {
  const { isMobile } = usePlatformBreakpoint();
  const { T, t } = useI18n();

  const table = Table.useTable();

  if (!Object.keys(table).length) {
    throw new Error('UsersListResultsTableData has to be used within <Table>');
  }
  if (table.getState().globalFilter && table.getRowCount() === 0) {
    return (
      <EmptyResults
        title={t("There are no users assigned to this scheduling instance that match your search.",

        {}, {
          project: 'enterprise-dashboard' })
        }
        subtitle={t("Try another search.", {}, { project: 'enterprise-dashboard' })} />);


  }

  if (!hasLoaded) {
    return (
      <Flex height="100%" justifyContent="center">
        <AcuityLoader />
      </Flex>);

  }

  // Note: This should never be true because by default there should always be at least 1 user assigned to an instance
  // (the org manager), but just in case...
  if (hasLoaded && !schedulingInstanceUsers.length) {
    return (
      <EmptyResults
        title={t("There are no users assigned to this scheduling instance.", {}, { project: 'enterprise-dashboard' })} />);


  }

  return (
    <>
      {!isMobile && <Table.List />}
      {isMobile &&
      <Table.Grid
        sx={{
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          marginTop: 3
        }}
        renderRecord={({ record }: TableGridRecordProps<OrgUser>) => {
          if (!record) {
            return null;
          }

          const cells = Object.fromEntries(record.getVisibleCells().map((cell) => [cell.column.id, cell]));
          return (
            <Table.Grid.Record>
                <Flex>
                  <Flex flexDirection="column" gap={1} p={3} width="100%">
                    <Flex flexDirection="column">
                      <Flex justifyContent="space-between">
                        <Table.Grid.Record.Cell cell={cells.name} />
                        <Table.Grid.Record.Cell cell={cells.id} />
                      </Flex>
                    </Flex>
                    <Flex gap={2} alignItems="center">
                      <Text.Caption fontSize={3}>
                        <T project="enterprise-dashboard">{"Role"}</T>
                      </Text.Caption>
                      <Table.Grid.Record.Cell cell={cells.role} />
                    </Flex>
                    <Flex gap={2} alignItems="center">
                      <Text.Caption fontSize={3}>
                        <T project="enterprise-dashboard">{"Status"}</T>
                      </Text.Caption>
                      <Table.Grid.Record.Cell mt={-1} cell={cells.status} />
                    </Flex>
                  </Flex>
                </Flex>
              </Table.Grid.Record>);

        }} />

      }
    </>);

};